import { useParams, Link, Navigate } from "react-router-dom";
import { useMemo } from "react";
import Layout from "@/components/site/Layout";
import Seo from "@/components/site/Seo";
import FaqBlock from "@/components/site/FaqBlock";
import ShareLocationButton from "@/components/site/ShareLocationButton";
import { getService, SERVICE_SLUGS } from "@/config/serviceContent";
import { CITIES } from "@/config/cities";
import { PHONE_DISPLAY, PHONE_TEL } from "@/config/site";
import { serviceSchema, localBusinessSchema, faqSchema, breadcrumbSchema } from "@/lib/schema";
import { Phone, MapPin, CheckCircle2, ArrowRight, Star } from "lucide-react";
import NeedHelpBlock from "@/components/site/NeedHelpBlock";
import { SERVICE_LONG } from "@/config/longContent";

export default function ServicePage({ overrideSlug }) {
  const params = useParams();
  const slug = overrideSlug || params.slug;
  const svc = getService(slug);

  // Memoize the "other services" list so the filter+map only runs when the
  // current service changes. Computed before the early-return null guard so
  // hooks are always called in the same order.
  const currentSlug = svc?.slug || "";
  const otherServices = useMemo(
    () => SERVICE_SLUGS.filter((s) => s !== currentSlug).map((s) => getService(s)).filter(Boolean),
    [currentSlug]
  );

  if (!svc) return <Navigate to="/" replace />;

  const url = typeof window !== "undefined" ? window.location.href : `/services/${svc.slug}`;
  const schemas = [
    localBusinessSchema({ url }),
    serviceSchema({ service: svc, url }),
    faqSchema(svc.faqs),
    breadcrumbSchema([
      { name: "Home", url: "/" },
      { name: svc.title, url: svc.path },
    ]),
  ].filter(Boolean);

  // Stabilize the FAQ array so <FaqBlock> doesn't see a new reference each render.
  const faqItems = [
    ...svc.faqs,
    ...(SERVICE_LONG[svc.slug]?.extraFaqs || []),
  ];

  return (
    <Layout>
      <Seo
        title={svc.metaTitle}
        description={svc.metaDesc}
        canonical={url}
        jsonLd={schemas}
      />

      {/* Hero */}
      <section className="bg-[#0A0A0A] border-b border-yellow-400/30 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <nav className="flex items-center gap-2 text-xs font-display uppercase tracking-widest text-zinc-500 mb-4">
              <Link to="/" className="hover:text-yellow-400">Home</Link>
              <span>/</span>
              <span className="text-yellow-400">{svc.title}</span>
            </nav>
            <h1 className="font-display font-black uppercase text-white text-4xl sm:text-6xl leading-[0.95] tracking-tight">
              {svc.h1}
            </h1>
            <p className="mt-5 text-zinc-300 text-lg max-w-2xl">{svc.hero}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid={`service-${svc.slug}-call-hero`}
                className="inline-flex items-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-6 py-4 text-base border-2 border-black bm-hard-shadow min-h-[56px]"
              >
                <Phone className="h-5 w-5" strokeWidth={3} /> Call {PHONE_DISPLAY}
              </a>
              <ShareLocationButton
                testId={`service-${svc.slug}-share`}
                variant="ghost"
                label="Share My Location"
                className="text-base"
              />
            </div>
          </div>
          <div className="lg:col-span-4 lg:justify-self-end">
            <div className="bg-yellow-400 text-slate-900 border-2 border-black bm-hard-shadow p-6">
              <div className="font-display uppercase tracking-[0.2em] text-xs">Available 24/7</div>
              <div className="mt-2 font-display font-black text-3xl leading-tight">
                Priced honestly on the phone.
              </div>
              <Link
                to="/#pricing"
                className="mt-4 inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-black text-yellow-400 font-display font-black uppercase tracking-wider px-4 py-3 border-2 border-black text-sm"
              >
                See Rates <ArrowRight className="h-4 w-4" strokeWidth={3} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Intro + benefits */}
      <section className="py-16 sm:py-20 border-b-2 border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <p className="text-zinc-300 text-lg leading-relaxed">{svc.intro}</p>
            <h2 className="mt-10 font-display font-black uppercase text-white text-3xl">Why call us for this</h2>
            <ul className="mt-4 space-y-3">
              {svc.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-zinc-300">
                  <CheckCircle2 className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" strokeWidth={2.5} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            {svc.situations && (
              <>
                <h2 className="mt-10 font-display font-black uppercase text-white text-3xl">Common situations</h2>
                <ul className="mt-4 grid sm:grid-cols-2 gap-3">
                  {svc.situations.map((s) => (
                    <li key={s} className="bg-[#0A0A0A] border-2 border-white/10 px-4 py-3 text-sm text-zinc-300">
                      {s}
                    </li>
                  ))}
                </ul>
              </>
            )}

            {SERVICE_LONG[svc.slug] && (
              <>
                <p className="mt-10 text-zinc-300 text-base leading-relaxed">
                  {SERVICE_LONG[svc.slug].longIntro}
                </p>
                {SERVICE_LONG[svc.slug].body.map((para) => (
                  <p key={para.slice(0, 60)} className="mt-4 text-zinc-300 text-base leading-relaxed">{para}</p>
                ))}

                <h2 className="mt-10 font-display font-black uppercase text-white text-3xl">Why call BM Towing for this</h2>
                <ul className="mt-4 space-y-3">
                  {SERVICE_LONG[svc.slug].whyUs.map((w) => (
                    <li key={w} className="flex items-start gap-3 text-zinc-300">
                      <Star className="h-5 w-5 text-yellow-400 mt-0.5 shrink-0" fill="#fbbf24" strokeWidth={1.5} />
                      <span>{w}</span>
                    </li>
                  ))}
                </ul>

                <h2 className="mt-10 font-display font-black uppercase text-white text-3xl">Pricing</h2>
                <p className="mt-3 text-zinc-300 text-base leading-relaxed">
                  {SERVICE_LONG[svc.slug].pricingNote}
                </p>
              </>
            )}
          </div>
          <aside className="lg:col-span-5">
            <div className="lux-glass-strong border-yellow-400/40 rounded-2xl p-6">
              <div className="font-display uppercase tracking-[0.2em] text-yellow-400 text-xs">Serving these cities</div>
              <ul className="mt-3 grid grid-cols-2 gap-2">
                {Object.values(CITIES).map((c) => (
                  <li key={c.slug}>
                    <Link
                      to={c.path}
                      data-testid={`service-${svc.slug}-city-${c.slug}`}
                      className="flex items-center gap-2 text-zinc-300 hover:text-yellow-400 border-2 border-white/10 hover:border-yellow-400 px-3 py-2 text-sm"
                    >
                      <MapPin className="h-4 w-4 text-yellow-400" />
                      <span className="font-display uppercase tracking-widest text-xs">{c.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <a
                href={`tel:${PHONE_TEL}`}
                data-testid={`service-${svc.slug}-call-side`}
                className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-display font-black uppercase tracking-wider px-4 py-3 border-2 border-black"
              >
                <Phone className="h-5 w-5" strokeWidth={3} /> Call Now
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 sm:py-20 border-b-2 border-white/5 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">FAQ</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl">
            About {svc.title.toLowerCase()}
          </h2>
          <div className="mt-6">
            <FaqBlock
              faqs={faqItems}
              testId={`service-${svc.slug}-faq`}
            />
          </div>
        </div>
      </section>

      {/* Other services + big CTA */}
      <NeedHelpBlock excludeSlug={svc.slug} testId={`service-${svc.slug}-needhelp`} />

      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-display uppercase tracking-[0.3em] text-yellow-400 text-sm">Explore</div>
          <h2 className="mt-2 font-display font-black uppercase text-white text-3xl sm:text-4xl">
            Other services we offer
          </h2>
          <ul className="mt-6 grid grid-cols-2 sm:grid-cols-3 gap-3">
            {otherServices.map((other) => (
              <li key={other.slug}>
                <Link
                  to={other.path}
                  data-testid={`service-${svc.slug}-related-${other.slug}`}
                  className="block bg-[#0A0A0A] border-2 border-white/10 hover:border-yellow-400 px-4 py-3 text-zinc-300 hover:text-white transition-colors"
                >
                  <span className="font-display font-black uppercase text-sm">{other.title}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </Layout>
  );
}
